import "./Row.scss";
import React, { useEffect, useRef, useState } from "react";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Link } from "react-router-dom";
import { buildMediaUrl, getDirectPlayPath, getTitlePath } from "../services/streamApi";

function Row({ title, subtitle, items = [], onMoreInfo }) {
  const scrollerRef = useRef(null);
  const dragStateRef = useRef({
    active: false,
    startX: 0,
    scrollLeft: 0,
    moved: false,
  });
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    const element = scrollerRef.current;
    if (!element) return undefined;

    const updatePaddles = () => {
      setCanScrollLeft(element.scrollLeft > 8);
      setCanScrollRight(element.scrollLeft + element.clientWidth < element.scrollWidth - 8);
    };

    updatePaddles();
    element.addEventListener("scroll", updatePaddles, { passive: true });
    window.addEventListener("resize", updatePaddles);

    return () => {
      element.removeEventListener("scroll", updatePaddles);
      window.removeEventListener("resize", updatePaddles);
    };
  }, [items.length]);

  if (!items.length) return null;

  const scrollByPage = (direction) => {
    const element = scrollerRef.current;
    if (!element) return;
    element.scrollBy({
      left: direction * Math.round(element.clientWidth * 0.88),
      behavior: "smooth",
    });
  };

  const handlePointerDown = (event) => {
    if (event.pointerType === "touch") return;
    const element = scrollerRef.current;
    if (!element) return;

    dragStateRef.current = {
      active: true,
      startX: event.clientX,
      scrollLeft: element.scrollLeft,
      moved: false,
    };

    element.classList.add("row__images--dragging");
  };

  const handlePointerMove = (event) => {
    const element = scrollerRef.current;
    const state = dragStateRef.current;
    if (!element || !state.active) return;

    const deltaX = event.clientX - state.startX;
    if (Math.abs(deltaX) > 5) {
      state.moved = true;
    }

    element.scrollLeft = state.scrollLeft - deltaX;
  };

  const handlePointerUp = () => {
    const element = scrollerRef.current;
    dragStateRef.current.active = false;
    if (element) {
      element.classList.remove("row__images--dragging");
    }

    window.setTimeout(() => {
      dragStateRef.current.moved = false;
    }, 0);
  };

  return (
    <section className="row">
      <div className="row__heading">
        <div>
          <h2 className="row__title">{title}</h2>
          {subtitle ? <p className="row__subtitle">{subtitle}</p> : null}
        </div>
      </div>

      <div className="row__rail">
        <button
          className={`row__paddle row__paddle--left ${canScrollLeft ? "is-visible" : ""}`}
          onClick={() => scrollByPage(-1)}
          aria-label="Defiler a gauche"
        >
          <ChevronLeftIcon />
        </button>

        <div
          ref={scrollerRef}
          className="row__images"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
          onClickCapture={(event) => {
            if (dragStateRef.current.moved) {
              event.preventDefault();
              event.stopPropagation();
            }
          }}
        >
          {items.map((item) => (
            <article key={item.id} className="row__card">
              {item.rankingLabel ? <span className="row__badge row__badge--ranking">{item.rankingLabel}</span> : null}
              {item.isNewRelease ? <span className="row__badge">Nouveau</span> : null}
            <Link to={getTitlePath(item.id)} className="row__imageLink">
              <img
                src={buildMediaUrl(item.backdropPath || item.posterPath || item.imagePath)}
                className="row__image"
                alt={item.title}
              />
            </Link>

            <div className="row__overlay">
              <div className="row__actions">
                <Link to={getDirectPlayPath(item)} className="row__action row__action--play">
                  <PlayArrowIcon />
                </Link>
                <button className="row__action" onClick={() => onMoreInfo?.(item)}>
                  <InfoOutlinedIcon />
                </button>
              </div>

              <h3>{item.title}</h3>
              <p>{item.categoryLabel || item.type}</p>
              <div className="row__meta">
                {item.badges?.map((badge) => (
                  <span key={badge}>{badge}</span>
                ))}
                {item.durationLabel ? <span>{item.durationLabel}</span> : null}
                {item.matchScore ? <span>{item.matchScore}% Match</span> : null}
              </div>
            </div>

            {item.resume ? (
              <div className="row__progress">
                <span style={{ width: `${item.resume.percent}%` }} />
              </div>
            ) : null}
            </article>
          ))}
        </div>

        <button
          className={`row__paddle row__paddle--right ${canScrollRight ? "is-visible" : ""}`}
          onClick={() => scrollByPage(1)}
          aria-label="Defiler a droite"
        >
          <ChevronRightIcon />
        </button>
      </div>
    </section>
  );
}

export default Row;
